import React from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Button,
  CircularProgress,
  Paper,
} from "@mui/material";
import { Refresh, PlayArrow, SportsEsports, Add } from "@mui/icons-material";
import { Chessboard } from "react-chessboard";
import { CurrentGame } from "../lichess/LichessClient";
import { Me } from "../lichess/LichessAuth";

interface CurrentGamesGridProps {
  me?: Me | null;
  currentGames: CurrentGame[];
  isLoading: boolean;
  onRefresh: () => void;
  onSelectGame: (gameId: string) => void;
  onGoToNewGame: () => void;
}

export const CurrentGamesGrid: React.FC<CurrentGamesGridProps> = ({
  me,
  currentGames,
  isLoading,
  onRefresh,
  onSelectGame,
  onGoToNewGame,
}) => {
  if (!me) {
    return (
      <Paper
        sx={{
          p: 4,
          textAlign: "center",
          backgroundColor: "#22272e",
          color: "white",
          borderRadius: 3,
          maxWidth: 600,
          mx: "auto",
        }}
      >
        <SportsEsports sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Lichess Account Required
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please connect your Lichess account to view and play your active games.
        </Typography>
        <Button variant="contained" color="primary" href="/connect">
          Connect Lichess
        </Button>
      </Paper>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      {/* Grid Header & Controls */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        pb={1.5}
        borderBottom="1px solid rgba(255, 255, 255, 0.1)"
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography variant="h5" fontWeight="bold" color="white">
            Active Games
          </Typography>
          <Chip
            label={currentGames.length}
            size="small"
            color={currentGames.length > 0 ? "primary" : "default"}
            sx={{ fontWeight: "bold", borderRadius: "12px" }}
          />
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Refresh active games list" arrow>
            <span>
              <IconButton
                onClick={onRefresh}
                disabled={isLoading}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255, 255, 255, 0.05)",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.15)" },
                }}
              >
                {isLoading ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
              </IconButton>
            </span>
          </Tooltip>

          <Button
            variant="outlined"
            size="small"
            startIcon={<Add />}
            onClick={onGoToNewGame}
            sx={{
              color: "white",
              borderColor: "rgba(255, 255, 255, 0.3)",
              textTransform: "none",
              "&:hover": { borderColor: "primary.main" },
            }}
          >
            New Game
          </Button>
        </Box>
      </Box>

      {/* Empty State */}
      {currentGames.length === 0 && !isLoading ? (
        <Paper
          sx={{
            p: 6,
            textAlign: "center",
            backgroundColor: "#22272e",
            color: "white",
            borderRadius: 3,
            border: "1px dashed rgba(255, 255, 255, 0.2)",
          }}
        >
          <SportsEsports sx={{ fontSize: 56, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" gutterBottom fontWeight="bold">
            No Active Games
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: "auto" }}>
            You don't have any ongoing games on Lichess. Start a new quick game or challenge the AI to begin playing!
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={onGoToNewGame}
            sx={{ textTransform: "none", px: 3, py: 1 }}
          >
            Create New Game
          </Button>
        </Paper>
      ) : (
        /* Games Grid */
        <Grid container spacing={3}>
          {currentGames.map((game) => {
            const isUserTurn = game.isMyTurn;
            const playerColor = (game.color === "black" ? "black" : "white") as "white" | "black";
            const opponentName = game.opponent?.username || "Opponent";
            const opponentRating = game.opponent?.rating ? ` (${game.opponent.rating})` : "";

            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={game.gameId}>
                <Card
                  sx={{
                    backgroundColor: "#22272e",
                    color: "white",
                    borderRadius: 3,
                    border: isUserTurn
                      ? "2px solid #22c55e"
                      : "1px solid rgba(255, 255, 255, 0.1)",
                    boxShadow: isUserTurn
                      ? "0 0 15px rgba(34, 197, 94, 0.2)"
                      : "0 4px 12px rgba(0, 0, 0, 0.3)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease, border 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
                      borderColor: isUserTurn ? "#22c55e" : "primary.main",
                    },
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <CardHeader
                    title={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ maxWidth: 180 }}>
                          vs {opponentName}{opponentRating}
                        </Typography>
                        <Chip
                          label={game.speed || "Live"}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                            color: "white",
                            fontSize: "0.75rem",
                            height: 22,
                          }}
                        />
                      </Box>
                    }
                    sx={{ pb: 1, pt: 2, px: 2 }}
                  />

                  {/* Mini View-Only Chessboard */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() => onSelectGame(game.gameId)}
                  >
                    <Box
                      sx={{
                        width: 220,
                        height: 220,
                        borderRadius: "8px",
                        overflow: "hidden",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.5)",
                        pointerEvents: "none", // View only!
                      }}
                    >
                      <Chessboard
                        options={{
                          position: game.fen || "startpos",
                          boardOrientation: playerColor,
                          canDragPiece: () => false,
                          allowDragOffBoard: false,
                          darkSquareStyle: { backgroundColor: "#4b7399" },
                          lightSquareStyle: { backgroundColor: "#eaedf1" },
                        }}
                      />
                    </Box>
                  </Box>

                  <CardContent sx={{ pt: 1, pb: 2, px: 2, mt: "auto" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                      <Chip
                        label={playerColor === "white" ? "⚪ White" : "⚫ Black"}
                        size="small"
                        variant="outlined"
                        sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}
                      />
                      <Chip
                        label={isUserTurn ? "Your Turn" : "Opponent's Turn"}
                        size="small"
                        color={isUserTurn ? "success" : "default"}
                        sx={{ fontWeight: "bold" }}
                      />
                    </Box>

                    {game.lastMove && (
                      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                        Last move: <strong>{game.lastMove}</strong>
                      </Typography>
                    )}

                    <Button
                      variant={isUserTurn ? "contained" : "outlined"}
                      color={isUserTurn ? "success" : "primary"}
                      fullWidth
                      startIcon={<PlayArrow />}
                      onClick={() => onSelectGame(game.gameId)}
                      sx={{
                        textTransform: "none",
                        fontWeight: "bold",
                        borderRadius: 2,
                      }}
                    >
                      {isUserTurn ? "Play Move" : "View Board"}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default CurrentGamesGrid;
